import React from 'react';
import { SplitContainer, ToastContext, Button } from '@weezevent/nacre';
import InventoriesStep from '../components/InventoriesStep';
import SessionsStep, { dateFormatTimezone } from '../components/SessionsStep';
import OrderStep from '../components/OrderStep';
import AttendeesStep from '../components/AttendeesStep';
import BuyerStep from '../components/BuyerStep';
import Summary from '../components/Summary';
import Stepper from './Stepper';
import { fetcher, useForm } from '@weezevent/weezjs-core';
import RatesTreeModel from '../../../../models/ratesTreeModel';
import moment from 'moment-timezone';
import QuotasModel from '../../../../models/quotasModel';
import { Category, Inventory, Session, Field, Form, FormEntry } from '../../../../models';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import css from '../styles.module.css';
import Timer from './Summary/Timer';
import { Booking } from '../../../../models/bookingModel';
import { ORIGIN_ADD_MANUAL, ORIGIN_STOCK_PRINT } from '../../../../models/order';
import { useSegment } from '../../../../components/tracking/trackingProvider';
import { Loader } from 'semantic-ui-react';
import InventorySelector from './InventoriesStep/InventorySelector';
import { computeQuota, getUpdatedQuota, getQuotaByRate, formateRates } from './utils';

function isUseInventories(event) {
    if (event?.is_seated) {
        return true;
    }
    if (event.use_sessions) {
        return false;
    }
    if (event.inventories.length > 0) {
        return true;
    }

    return false;
}
export function DatesFakeSeatingStep({ event, inventories, form, setFormObject, onChangeSteps, step, isInvitations = false }) {
    const { t } = useTranslation();

    const onSelectInventory = React.useCallback(
        selectedInventories => {
            let selected_inventories = inventories?.filter(invt => selectedInventories.includes(invt.pk));

            setFormObject({ ...form, inventories: selected_inventories, selected_rates: [] });
        },
        [form, setFormObject, inventories]
    );

    return (
        <>
            <InventorySelector
                title={t('attendees.addAttendees.withSeats.dates-title')}
                onChange={onSelectInventory}
                form={form}
                inventories={inventories}
                event={event}
                isInvitations={isInvitations}
            />
            <Button
                className={css['button-continue']}
                primary
                disabled={!form.inventories?.length}
                label={t('ticket.common.action.continue')}
                onClick={() => onChangeSteps(step + 1)}
            />
        </>
    );
}

export default function StepsManager({ organization, event, isAddAttendees }) {
    const { t } = useTranslation();
    const { track } = useSegment();
    const history = useHistory();
    let [computedQuota, setComputedQuotas] = React.useState({});
    let [bookingSelectionAuto, setBookingSelectionAuto] = React.useState(false);
    let [buyerFields, setBuyerFields] = React.useState([]);
    let [loadBuyerFields, setLoadingBuyerField] = React.useState(false);
    const [activeInventories, setActiveInventories] = React.useState([]);
    const [currentInventory, setCurrentInventory] = React.useState();
    const [previousInventory, setPreviousInventory] = React.useState();
    let [selectedZone, setSelectedZone] = React.useState();
    let [previousZone, setPreviousZone] = React.useState();
    let [fullScreenMode, setFullScreenMode] = React.useState(false);
    let [step, setStep] = React.useState(1);
    const [backToSeats, setBackToSeats] = React.useState(false);
    const toast = React.useContext(ToastContext);
    const [datesIds, setDatesIds] = React.useState();
    const [selectedDay, setSelectedDay] = React.useState(null);
    const currencyOutput = Intl.NumberFormat(event?.default_language, {
        style: 'currency',
        currency: event.currency
    });

    const [form, setForm, setFormObject] = useForm(
        {
            totalAmount: 0,
            step: 1,
            date: null,
            currency: event?.currency,
            status: 'valid',
            reservation: 0,
            statusColor: 'green',
            languageLabel: event?.default_language,
            use_sessions: event?.use_sessions,
            use_inventories: isUseInventories(event),
            selected_rates: [],
            inventories: [],
            sessions: [],
            attendees: [],
            comment: null,
            orderDate: moment(),
            orderDateModified: false,
            validationModal: false,
            submitting: false,
            selectedZone: '',
            previousZone: '',
            sendEmail: false,
            sendPaymentRequest: false,
            downloadPDF: false,
            buyer_form: {},
            payment_type: ORIGIN_ADD_MANUAL,
            billing_form: {
                company: '',
                vat_number: '',
                address: '',
                city: '',
                zip_code: '',
                country: '',
                region: '',
                information: ''
            }
        },
        {}
    );

    const ratesCount = React.useMemo(() => {
        if (!form.use_inventories) {
            // Count rate * quantity
            let counter = 0;
            form.selected_rates.forEach(selected_rate => {
                counter += selected_rate.quantity;
            });

            return counter;
        }
        return form.attendees.length;
    }, [form, form.selected_rates, form.attendees]);

    const attendeesCount = React.useMemo(() => {
        return form.attendees.length;
    }, [form, form.attendees]);

    const [sessionTree, loadingSessions] = Session.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            launch: organization.pk && event.pk && event?.use_sessions,
            sync: true
        },
        [organization, event]
    );

    const [quotasModel, loadingQuotas] = QuotasModel.useApiModel(
        {
            strict: true,
            launch: Boolean(!event.use_sessions || (event.use_sessions && datesIds?.length)),
            organizationId: organization.pk,
            eventId: event.pk,
            getForRates: true,
            query: {
                limit: 9999,
                ...(event.use_sessions && datesIds?.length ? { dates_ids: `[${datesIds}]` } : { id_date: 0 })
            }
        },
        [organization, event, datesIds]
    );

    const [ratesTree, loadingRates] = RatesTreeModel.useApiModel(
        {
            strict: true,
            organizationId: organization.pk,
            eventId: event.pk,
            launch: organization.pk && event.pk,
            sync: true
        },
        [organization, event]
    );

    const [inventories, loadingInventories] = Inventory.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            chartId: event.chart,
            defaultLoading: false,
            launch: Boolean((isUseInventories(event) && !event.isRealSeatingWithSessions()) || (selectedDay && event.isRealSeatingWithSessions())),
            isFakeSeating: true,
            query: {
                ...(event.isRealSeatingWithSessions() && selectedDay && { date: dateFormatTimezone(selectedDay, event.timezone, moment) })
            },
            onSuccess: invt => {
                if (event.isFakeSeating()) {
                    setBookingSelectionAuto(true);
                    if (invt.length === 1) {
                        setForm('inventories', invt);
                    }
                }
                let active_inventories = invt.filter(i => i.active && i.event === event.id);
                setActiveInventories(active_inventories);
                if (!event.isRealSeatingWithSessions()) {
                    setCurrentInventory(active_inventories[0]?.object?.uuid);
                    setPreviousInventory(active_inventories[0]?.object?.uuid);
                }
            }
        },
        [organization, event, selectedDay]
    );

    const [categoriesModel, loadingCategories] = Category.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            chartId: event.chart,
            launch: isUseInventories(event)
        },
        [organization, event]
    );

    const quota = React.useMemo(() => {
        return getUpdatedQuota(computedQuota, form.sessions, form.selected_rates);
    }, [computedQuota, form]);

    const searchRateIntoQuota = React.useCallback(
        ({ rateId, groupId = null, sessionId = null }) => {
            return getQuotaByRate(rateId, groupId, sessionId, quota, event);
        },
        [quota, event]
    );

    const rates = React.useMemo(() => {
        return formateRates(computedQuota, ratesTree, selectedDay && form?.sessions?.length ? form.sessions : sessionTree, searchRateIntoQuota);
    }, [computedQuota, ratesTree, sessionTree, form.sessions, selectedDay, searchRateIntoQuota]);

    let categories = React.useMemo(() => {
        /**
         * Construct categories and add rates informations.
         */
        let categoriesTmp = [];
        if (rates && categoriesModel) {
            categoriesTmp = categoriesModel.map(cat => {
                let categoryRatesMapping = rates
                    .filter(rate => rate.resolved_category === cat.pk)
                    .map(rate => {
                        return {
                            price: rate.resolved_price,
                            priceText: currencyOutput.format(rate.resolved_price),
                            rate_obj: rate
                        };
                    });
                cat.rates = categoryRatesMapping;
                return cat;
            });
        }
        return categoriesTmp;
    }, [rates, categoriesModel]);

    const onChangeSelectedRates = React.useCallback(
        (selected_rates = []) => {
            setForm('selected_rates', selected_rates);
        },
        [setForm]
    );

    const computeQuotaAddAttendee = React.useCallback(() => {
        return setComputedQuotas(computeQuota(ratesTree, quotasModel, event));
    }, [ratesTree, quotasModel, event]);

    React.useEffect(() => {
        setForm('payment_type', isAddAttendees ? ORIGIN_ADD_MANUAL : ORIGIN_STOCK_PRINT);
    }, [isAddAttendees]);

    React.useEffect(() => {
        if (!loadingRates && !loadingQuotas && !(event?.use_sessions ? loadingSessions : false)) {
            computeQuotaAddAttendee();
        }
    }, [loadingRates, loadingQuotas, loadingSessions]);

    const onChangeSelectedSessions = React.useCallback(
        sessions => {
            if (event.isRealSeatingWithSessions()) {
                try {
                    let _filteredInventory = null;
                    if (sessions.length) {
                        let inventoryUuids = sessions.reduce((acc, s) => {
                            acc.push(s.value);
                            return acc;
                        }, []);

                        //We have a query for sessions and a query for inventories, but these are
                        //actually inventories in both cases
                        //To be able to use the current flow seated, we filter the inventories from the
                        //session uuids.
                        //only one session selectable to we use find to optimize and not go through all the inventories
                        _filteredInventory = inventories.find(inv => inventoryUuids.includes(inv.uuid));
                        if (!_filteredInventory) {
                            throw new Error(`[Inventory] no matching Inventory found for sessions ${inventoryUuids}`);
                        }
                        //it's deliberate not to put a condition here, to highlight the problem
                        setCurrentInventory(_filteredInventory.uuid);
                        setPreviousInventory(_filteredInventory.uuid);
                    }
                    setFormObject({ ...form, sessions: sessions, inventories: _filteredInventory ? [_filteredInventory] : [], selected_rates: [] });
                } catch (err) {
                    console.error(err);
                    toast.error(t('ticket.rates.inventories-placeholder'));
                }
            } else {
                setFormObject({ ...form, sessions: sessions, selected_rates: [] });
            }
        },
        [form, setFormObject, inventories, event, t, toast]
    );

    const onChangeSteps = React.useCallback(
        stepToGo => {
            setStep(Math.max(1, stepToGo));
        },
        [setStep]
    );

    const onConstructAttendees = React.useCallback(
        (bookings = null) => {
            // Construct attendees
            // Called on OrderStep, on click on "continu" button. We have to create attendees
            // according to rates selected, with the quantity.
            // On modify this step, we have to keep olds attendees and whange only what we need.
            // FYI : Bookings can be non to distinct contruct attendee without booking and construct attendees with bookings

            let attendeesToPush = form.attendees;
            let rates_to_create = form.selected_rates
                .map(s_rate => {
                    // Get attendees already created for each rate
                    let attendees_already = attendeesToPush.filter(
                        attendee => attendee.rate_id === s_rate.rate.pk && attendee.sessionId === s_rate.sessionId && s_rate.inventory?.pk === attendee.booking_uuid?.inventory
                    );
                    if (attendees_already.length === s_rate.quantity) {
                        return null;
                    }

                    if (attendees_already.length > s_rate.quantity) {
                        return null;
                    }

                    return { ...s_rate, quantity: s_rate.quantity - attendees_already.length };
                })
                .filter(s_rate => s_rate);
            rates_to_create.forEach(selected_rate => {
                let books = null;
                if (bookings) {
                    books = bookings.filter(booking => booking && parseInt(booking.rate) === selected_rate.rate.pk);
                }
                for (let i = 0; i < selected_rate.quantity; i++) {
                    // take always the first booking and delete it from books after.
                    let book = books ? books[0] : null;
                    if (bookings && !book) {
                        // if we have bookings but no attendees, we had an error ...
                        continue;
                    }
                    attendeesToPush.push({
                        id: attendeesToPush.length + 1, // We put a fake id to recognize which attendee is updated
                        first_name: '',
                        last_name: '',
                        event_id: event.pk,
                        email: '',
                        form: {},
                        category: selected_rate.rate.category,
                        paid_price: selected_rate.price,
                        rate_id: selected_rate.rate.id,
                        booking_uuid: book ? book : null,
                        session_uuid: selected_rate.rate.sessionId
                    });
                    if (book) {
                        // Delete index 0 of books to do not re-use this booking
                        books.splice(0, 1);
                    }
                }
            });

            // Get from buyer
            setLoadingBuyerField(true);
            // Always take all selected rates to get buyer form
            Form.getByRates({
                organizationId: organization.pk,
                rateIds: form.selected_rates.map(selected_rate => selected_rate.rate.id),
                usage: 'buyer'
            })
                .then(({ response }) => {
                    setBuyerFields(
                        response.map(field => {
                            let fe = new FormEntry();
                            fe.usage = 'buyer';
                            fe.required = false;
                            fe.form_field = Field.fromJSON(field.form_field);
                            return fe;
                        })
                    );
                    setLoadingBuyerField(false);
                })
                .catch(e => {
                    setLoadingBuyerField(false);
                });

            // Get rates which do not have any form_attendee
            let rates_wo_form = form.selected_rates.filter(selected_rates => {
                if (!('attendee_form' in selected_rates)) {
                    return true;
                }
                return false;
            });

            // Get form_attendee for each rate, and store it into selected_rates
            let tmpSelectedRates = [...form.selected_rates];
            if (rates_wo_form.length) {
                let usage = 'attendee';
                Form.getByRates({
                    organizationId: organization.pk,
                    rateIds: rates_wo_form.map(selected_rate => selected_rate.rate.id),
                    usage: usage
                })
                    .then(({ response }) => {
                        Object.keys(response).forEach(rateId => {
                            let selected_rate = tmpSelectedRates.find(s_rate => s_rate.rate.id === parseInt(rateId));
                            if (!selected_rate) {
                                return;
                            }
                            if (!('attendee_form' in selected_rate)) {
                                selected_rate.attendee_form = [];
                            }
                            selected_rate.attendee_form = response[rateId]
                                .filter(entry => entry.usage === usage)
                                .map(item => {
                                    let fe = new FormEntry();
                                    fe.usage = usage;
                                    fe.required = false;
                                    fe.form_field = Field.fromJSON(item.form_field);
                                    return fe;
                                }, {});
                        });
                        setFormObject({ ...form, attendees: attendeesToPush, selected_rates: tmpSelectedRates });
                    })
                    .catch(e => {
                        toast.error(t(`partners.invitation.errors.${e.response.detail ? e.response.detail : 'unknownError'}`));
                    });
            } else {
                setFormObject({ ...form, attendees: attendeesToPush });
            }
        },
        [event, setFormObject, form, setForm, Form, form.selected_rates, setBuyerFields, setLoadingBuyerField]
    );

    const handleSubmit = React.useCallback(() => {
        setForm('submitting', true);
        let attendees = form.attendees.map(attendee => {
            let selected_rate = form.selected_rates.find(s_rate => s_rate.rate.id === attendee.rate_id);
            //we subtract the fees from the ticket price, because the apiInternal Menhir already adds them.
            //So we subtract the fee only if the ticket price is greater than or equal to the total fee
            let totalFees = selected_rate.rate.fees.reduce((acc, current) => acc + Number(current.amount), 0);
            let price = selected_rate.price >= totalFees ? selected_rate.price - totalFees : selected_rate.price;
            let data = {
                rate_id: attendee.rate_id,
                event_id: event.pk,
                return_ticket_url: true,
                paid_price: Math.round(price * 100) / 100,
                form: attendee.form
            };
            if (attendee.booking_uuid) {
                data['booking_uuid'] = attendee.booking_uuid.booking_uuid;
            }
            if (attendee.session_uuid) {
                data['session_uuid'] = attendee.session_uuid;
            }
            return data;
        });
        let payload = [
            {
                event_id: event.pk,
                buyer_form: form.buyer_form,
                sold_date: form.orderDate,
                channel_id: 0,
                attendees: attendees,
                status: form.status,
                reservation: form.reservation,
                language: form.languageLabel,
                payment_type: form.payment_type
            }
        ];

        if (Object.values(form.billing_form).some(ent => ent !== '')) {
            payload[0]['invoice'] = form.billing_form;
        }
        if (form.downloadPDF) {
            toast.info(t('ticket.accounting.modal.downloading'));
        }
        fetcher
            .post(`/ticket/organizations/${organization.id}/orders?download=${form.downloadPDF}&send_mail=${form.sendEmail}&send_payment_request=${form.sendPaymentRequest}`, {
                data: payload
            })
            .then(({ response }) => {
                if (form.downloadPDF) {
                    if ('url' in response[0] && response[0]?.url !== null) {
                        window.open(response[0].url, '_blank');
                    } else {
                        toast.error(t('partners.invitation.download_ticket_error'));
                    }
                }
                toast.success(t('attendee.order.success'));
            })
            .catch(e => {
                toast.error(t('attendee.order.failure'));
            })
            .finally(() => {
                setTimeout(() => {
                    window.location.reload();
                }, 1000);
            });
    }, [form, setForm, event, toast, history]);

    const isSubmitting = React.useMemo(() => {
        return form.submitting;
    }, [form.submitting]);

    const unbookBooking = React.useCallback(
        (attendees_to_unbook = [], reload = false) => {
            let booking = new Booking();
            let bookings_uuid_to_unbook = attendees_to_unbook.map(attendee => attendee.booking_uuid.booking_uuid);
            if (bookings_uuid_to_unbook.length) {
                booking
                    .unbook({ bookings: bookings_uuid_to_unbook, chartId: event.chart, organizationId: organization.pk })
                    .then(() => {
                        // Delete attendee and selected rates on success
                        let attendees = [...form.attendees];
                        let selectedRates = [...form.selected_rates];
                        let attendee_ids_to_delete = attendees_to_unbook.map(attendee => attendee.id);
                        attendee_ids_to_delete.forEach(id => {
                            let attendee_index = attendees.findIndex(att => att.id === id);
                            let attendee = attendees.find(att => att.id === id);
                            let selectedRate = selectedRates.find(sRate => sRate.rate.id === attendee.rate_id);

                            if (selectedRate) {
                                if (selectedRate.quantity > 1) {
                                    // If we have other attendees with this rate, juste decrement the quantity
                                    selectedRate.quantity -= 1;
                                } else {
                                    // Delete selected rates if attendee was the last
                                    let selectedRateIndex = selectedRates.findIndex(sRate => sRate === selectedRate);
                                    selectedRates.splice(selectedRateIndex, 1);
                                }
                            }
                            if (attendee_index >= 0) {
                                attendees.splice(attendee_index, 1);
                            }
                        });
                        setFormObject({ ...form, attendees: attendees, selected_rates: selectedRates });
                    })
                    .catch(e => {
                        // TODO ADRIEN : TOAST ERROR ON DELETE ?
                    })
                    .finally(() => {
                        if (reload) {
                            window.location.reload();
                        }
                    });
            }
        },
        [event, organization, form, setFormObject]
    );

    const timer = React.useMemo(() => {
        if (form.attendees.filter(attendee => attendee.booking_uuid).length) {
            // start timer on booking created
            return (
                <Timer
                    details={true}
                    form={form}
                    validityTimer={900}
                    onTimerReached={() => {
                        // Delete bookings and reload page
                        unbookBooking(form.attendees, true);
                    }}
                />
            );
        }
        return false;
    }, [form, unbookBooking]);

    const selectionSessionCount = React.useMemo(() => {
        return form.sessions?.length;
    }, [form]);

    const renderStepSessionOrInventory = React.useMemo(() => {
        let optionalSteps = [];

        if (event.use_sessions) {
            optionalSteps.push(
                <SessionsStep
                    name="sessions_step"
                    title={t('ticket.common.seances')}
                    subtitle={t('attendees.addAttendees.withSessions.title')}
                    inactive={`${t('attendees.addAttendees.withSessions.count')}: ${form.sessions?.length}`}
                    sessions={sessionTree}
                    form={form}
                    step={step}
                    onChangeSelectedSessions={onChangeSelectedSessions}
                    selectedDay={selectedDay}
                    setSelectedDay={setSelectedDay}
                    setDatesIds={setDatesIds}
                    onChangeSteps={onChangeSteps}
                    loading={loadingSessions}
                    loadingInventories={loadingInventories}
                />
            );
        }

        if (event.isFakeSeatingMultipleInventories()) {
            optionalSteps.push(
                <DatesFakeSeatingStep
                    title={t('attendees.addAttendees.withSeats.dates-title')}
                    subtitle={t('attendees.addAttendees.withSeats.dates-subtitle')}
                    inactive={t('attendees.addAttendees.withSeats.label-dates-selected', { count: form.inventories.length })}
                    inventories={inventories}
                    form={form}
                    setFormObject={setFormObject}
                    onChangeSteps={onChangeSteps}
                    step={step}
                />
            );
        }
        if (event.isRealSeating()) {
            optionalSteps.push(
                <InventoriesStep
                    name="inventories_step"
                    title={t('attendees.addAttendees.withSeats.title')}
                    subtitle={t('attendees.addAttendees.withSeats.subtitle')}
                    inactive={`${t('attendees.addAttendees.withSeats.mode')}: ${
                        bookingSelectionAuto ? t('attendees.addAttendees.withSeats.auto') : t('attendees.addAttendees.withSeats.manual')
                    }`}
                    form={form}
                    inventories={activeInventories}
                    categories={categories}
                    loading={loadingInventories && loadingCategories}
                    onChange={setForm}
                    rates={rates}
                    step={step}
                    previousInventory={previousInventory}
                    setPreviousInventory={setPreviousInventory}
                    currentInventory={currentInventory}
                    setCurrentInventory={setCurrentInventory}
                    selectedZone={selectedZone}
                    setSelectedZone={setSelectedZone}
                    previousZone={previousZone}
                    setPreviousZone={setPreviousZone}
                    bookingSelectionAuto={bookingSelectionAuto}
                    setBookingSelectionAuto={setBookingSelectionAuto}
                    onConstructAttendees={onConstructAttendees}
                    event={event}
                    fullScreenMode={fullScreenMode}
                    setFullScreenMode={setFullScreenMode}
                    onChangeAllForm={setFormObject}
                    onChangeSteps={onChangeSteps}
                    timer={timer}
                    unbookBooking={unbookBooking}
                    backToSeats={backToSeats}
                />
            );
        }

        return optionalSteps;
    }, [
        form,
        event,
        step,
        selectionSessionCount,
        bookingSelectionAuto,
        loadingInventories,
        loadingSessions,
        inventories,
        rates,
        sessionTree,
        onChangeSteps,
        onConstructAttendees,
        onChangeSelectedSessions,
        activeInventories,
        previousInventory,
        setPreviousInventory,
        currentInventory,
        setCurrentInventory,
        fullScreenMode,
        setFullScreenMode,
        selectedZone,
        setSelectedZone,
        previousZone,
        setPreviousZone
    ]);

    const onClickStepEdit = React.useCallback(
        stepIndex => {
            if (form.use_sessions || form.use_inventories) stepIndex--;
            const stepsToEdit = ['PLACEMENT', 'ORDER', 'ATTENDEES'];
            if (form.use_sessions && stepIndex === 0) track('ACTION > MODIFY SESSIONS', { 'Page Title': 'ADD ATTENDEES' });
            else if (stepIndex < stepsToEdit.length) track(`ACTION > MODIFY ${stepsToEdit[stepIndex]} INFORMATION`, { 'Page Title': 'ADD ATTENDEES' });
        },
        [form, track]
    );

    if (loadingInventories && isUseInventories(event) && !event.isRealSeatingWithSessions()) {
        return <Loader active />;
    }

    return (
        <SplitContainer className={css['summary-position']} size={'maxi'} additional={<Summary form={form} setForm={setForm} timer={timer} />} onlyDesktop rootId="add-attendees">
            <Stepper
                form={form}
                step={step}
                unbookAttendees={unbookBooking}
                onChangeForm={setFormObject}
                onClickEdit={onClickStepEdit}
                onChangeSteps={onChangeSteps}
                bookingSelectionAuto={bookingSelectionAuto}
                setFullScreenMode={setFullScreenMode}
                setSelectedZone={setSelectedZone}
                setPreviousZone={setPreviousZone}
                setBackToSeats={setBackToSeats}
            >
                {renderStepSessionOrInventory}
                <OrderStep
                    name="order_step"
                    title={t('attendees.addAttendees.withTarif.title')}
                    subtitle={t('attendees.addAttendees.withTarif.subtitle')}
                    inactive={`${t('attendees.addAttendees.withTarif.nexttitle')}: ${ratesCount}`}
                    form={form}
                    onChangeForm={setForm}
                    onChangeFormObject={setFormObject}
                    ratesTree={ratesTree}
                    rates={rates}
                    quotas={quota}
                    step={step}
                    event={event}
                    inventories={activeInventories}
                    loadBuyerFields={loadBuyerFields}
                    onChangeSteps={onChangeSteps}
                    onChangeSelectedRates={onChangeSelectedRates}
                    handleSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                    loadingRates={loadingRates}
                    bookingSelectionAuto={bookingSelectionAuto}
                    onConstructAttendees={onConstructAttendees}
                    unbookBooking={unbookBooking}
                    onChangeAllForm={setFormObject}
                    getQuotaForOneRate={searchRateIntoQuota}
                    computeQuota={computeQuotaAddAttendee}
                    isAddAttendees={isAddAttendees}
                />
                {form.payment_type !== ORIGIN_STOCK_PRINT && (
                    <AttendeesStep
                        name="attendees_step"
                        title={t('attendees.addAttendees.attendeesInformations')}
                        subtitle={t('attendees.addAttendees.attendeesTitle')}
                        inactive={`${t('attendees.addAttendees.count')} ${attendeesCount}`}
                        form={form}
                        step={step}
                        categories={categories}
                        onChangeForm={setForm}
                        handleSubmit={handleSubmit}
                        isSubmitting={isSubmitting}
                        inventories={inventories}
                        onChangeSteps={onChangeSteps}
                        setFullScreenMode={setFullScreenMode}
                        setSelectedZone={setSelectedZone}
                        setPreviousZone={setPreviousZone}
                        setBackToSeats={setBackToSeats}
                        bookingSelectionAuto={bookingSelectionAuto}
                    />
                )}
                <BuyerStep
                    name="buyer_step"
                    title={t('ticket.manager.orders.detail.buyerInfos')}
                    subtitle={t('attendees.addAttendees.buyerInformations')}
                    inactive={t('attendees.addAttendees.buyerInformations')}
                    form={form}
                    step={step}
                    onChangeSteps={onChangeSteps}
                    buyerFields={buyerFields}
                    onChangeForm={setForm}
                    loadBuyerFields={loadBuyerFields}
                    handleSubmit={handleSubmit}
                    isSubmitting={isSubmitting}
                />
            </Stepper>
        </SplitContainer>
    );
}
